import type {MaybeRef} from "vue";

import {computed, unref} from "vue";
import {useI18n} from "vue-i18n";
import {useStoryblokStories} from "./use-storyblok-stories";

type AsyncOptions = Parameters<typeof useStoryblokStories>[2];

type Params = {
	categories?: MaybeRef<string[]>;
	featured?: MaybeRef<boolean>;
	page?: MaybeRef<number>;
	perPage?: MaybeRef<number>;
	searchTerm?: MaybeRef<string | undefined>;
	extraFilters?: MaybeRef<Record<string, unknown>>;
};

export function useStoryblokArticles(
	{
		categories,
		featured,
		page = 1,
		perPage = 20,
		searchTerm = undefined,
		extraFilters = {},
	}: Params,
	asyncOptions: AsyncOptions = {},
) {
	const {t} = useI18n({
		useScope: "global",
	});

	const filterQuery = computed(() => {
		const categoriesString = unref(categories)?.join(",");
		const categoriesFilter = categoriesString
			? {
					category: {any_in_array: categoriesString},
				}
			: {};
		const featuredValue = unref(featured);
		const featuredFilter =
			featuredValue === undefined
				? {}
				: {
						featured: {is: featuredValue},
					};

		const filterValue = unref(extraFilters);
		const additionalFilter =
			filterValue === undefined
				? {}
				: {
						...filterValue,
					};
		return {
			// Rule out non-article page types

			component: {not_in: "sb-articles-lander"},
			...featuredFilter,
			...categoriesFilter,
			...additionalFilter,
		};
	});

	const fetchKey =
		`storyblok-category-articles:(${unref(categories)?.join("|")})` +
		`-featured:${unref(featured)}-perPage:${unref(perPage)}-filterQuery:${unref(extraFilters)}` +
		`-searchTerm:${unref(searchTerm)}`;

	return useStoryblokStories(
		fetchKey,
		{
			starts_with: t("storyblok.articles"),
			sort_by: "first_published_at:desc",
			page,
			per_page: perPage,
			is_startpage: false,
			filter_query: filterQuery,
			search_term: searchTerm,
		},
		asyncOptions,
	);
}
